/**
 * 1.3.0 the media portal's identity (stream P1's), for P3's main.tsx:
 *
 *   <PortalSignIn onSignedIn={(me) => …} />   (also the default export): the sign-in screens until signed in
 *   <PortalIdentity>{({ me, signOut, refreshMe }) => <TheRestOfThePortal … />}</PortalIdentity>   (the same, with /me)
 *   <AccountPage me={me} onSignOut={signOut} onChanged={refreshMe} />      (the account page, where P3 routes it)
 *
 * PortalIdentity shows the sign-in screens until a supplier is signed in: sign-in, the two-step code, enrolment
 * (required by default), forgot password, and the set-password pages the emails link to (/invitation?token=…,
 * /reset?token=…). While the portal is off it says so. A call that answers 401 later (the session ended: signed out
 * everywhere by staff, idle, disabled) should call `sessionEnded()`, which shows the sign-in again.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { PortalMeDTO, PortalPublicDTO, PortalSignInResult } from '@prism/contracts';
import { portalApi, PortalApiError } from '../client';
import { t } from '../i18n/en';
import { AuthFrame, CodeScreen, EnrolScreen, ForgotScreen, SetPasswordScreen, SignInScreen } from './screens';

export { AccountPage, EnrolPanel, problemText } from './screens';

export interface PortalSession {
  me: PortalMeDTO;
  /** Signs out on the server, then shows the sign-in. */
  signOut: () => Promise<void>;
  /** Reads /me again (after the account page changed something). */
  refreshMe: () => Promise<void>;
  /** The session ended elsewhere (a 401): shows the sign-in. */
  sessionEnded: () => void;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'off' }
  | { kind: 'signin'; notice?: string | null }
  | { kind: 'code' }
  | { kind: 'enrol' }
  | { kind: 'forgot' }
  | { kind: 'set'; mode: 'invitation' | 'reset'; token: string }
  | { kind: 'in'; me: PortalMeDTO };

/** The set-password page an email linked to, if this is one. */
function linkPhase(): Phase | null {
  const { pathname, search } = window.location;
  const token = new URLSearchParams(search).get('token');
  if (!token) return null;
  if (pathname === '/invitation') return { kind: 'set', mode: 'invitation', token };
  if (pathname === '/reset') return { kind: 'set', mode: 'reset', token };
  return null;
}

/**
 * Back to the portal's start, without the token in the address bar or the history. 1.3.1: the router (main.tsx's
 * BrowserRouter) does not see a bare replaceState, so it stayed on /invitation and, once signed in, showed "This page
 * is not here"; a popstate tells it the address changed (the router's own history state is kept).
 */
const leaveLink = () => {
  if (!window.location.search.includes('token=')) return;
  window.history.replaceState(window.history.state, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
};

type SignInPhase = Exclude<Phase, { kind: 'in' } | { kind: 'loading' }>;

/**
 * The sign-in screens (sign-in, the code, enrolment, forgot password, the set-password links) until a supplier is
 * signed in; then `onSignedIn` with their /me. It does not check an existing session first: show it when /me
 * answered 401 (PortalIdentity does that check).
 */
export function PortalSignIn({ onSignedIn, notice, pub: givenPub }: { onSignedIn: (me: PortalMeDTO) => void; notice?: string | null; pub?: PortalPublicDTO | null }) {
  const [pub, setPub] = useState<PortalPublicDTO | null>(givenPub ?? null);
  const [phase, setPhase] = useState<SignInPhase>(() => (linkPhase() as SignInPhase | null) ?? { kind: 'signin', notice: notice ?? null });
  useEffect(() => {
    if (givenPub !== undefined) return;
    void portalApi<PortalPublicDTO>('/public').then((p) => { setPub(p); if (!p.enabled) setPhase({ kind: 'off' }); }).catch(() => undefined);
  }, [givenPub]);
  const signedIn = async (me?: PortalMeDTO) => {
    try {
      onSignedIn(me ?? (await portalApi<PortalMeDTO>('/me')));
    } catch {
      setPhase({ kind: 'signin', notice: null });
    }
  };
  const onResult = (r: PortalSignInResult) => {
    leaveLink();
    if (r.next === 'done') void signedIn(r.me);
    else setPhase({ kind: r.next === 'code' ? 'code' : 'enrol' });
  };
  const toSignIn = (n: string | null = null) => { leaveLink(); setPhase({ kind: 'signin', notice: n }); };
  switch (phase.kind) {
    case 'off':
      return <AuthFrame pub={pub} testId="portal-off"><h1>{t('identity.disabledPortal')}</h1></AuthFrame>;
    case 'signin':
      return <SignInScreen pub={pub} notice={phase.notice} onResult={onResult} onForgot={() => setPhase({ kind: 'forgot' })} />;
    case 'code':
      return <CodeScreen pub={pub} onResult={onResult} onBack={() => toSignIn()} />;
    case 'enrol':
      return <EnrolScreen pub={pub} onDone={() => void signedIn()} onBack={() => toSignIn()} />;
    case 'forgot':
      return <ForgotScreen pub={pub} onBack={() => toSignIn()} />;
    case 'set':
      return <SetPasswordScreen pub={pub} mode={phase.mode} token={phase.token} onInvitation={onResult}
        onReset={() => toSignIn(t('identity.setPassword.saved'))} onBack={() => toSignIn()} />;
  }
}

export default PortalSignIn;

/** The sign-in screens, then `children` with the session (checks /me first; reloads into sign-in when it ends). */
export function PortalIdentity({ children }: { children: (s: PortalSession) => ReactNode }) {
  const [pub, setPub] = useState<PortalPublicDTO | null>(null);
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'off' } | { kind: 'out'; notice: string | null; n: number } | { kind: 'in'; me: PortalMeDTO }>({ kind: 'loading' });
  const out = (notice: string | null = null) => setState((s) => ({ kind: 'out', notice, n: (s.kind === 'out' ? s.n : 0) + 1 }));
  const loadMe = useCallback(async (): Promise<void> => {
    try {
      setState({ kind: 'in', me: await portalApi<PortalMeDTO>('/me') });
    } catch (err) {
      if (err instanceof PortalApiError && err.code === 'PORTAL_DISABLED') setState({ kind: 'off' });
      else out(err instanceof PortalApiError && err.code === 'SESSION_IDLE' ? t('identity.error.SESSION_IDLE') : null);
    }
  }, []);
  useEffect(() => {
    void (async () => {
      const p = await portalApi<PortalPublicDTO>('/public').catch(() => null);
      setPub(p);
      if (p && !p.enabled) return setState({ kind: 'off' });
      if (linkPhase()) return out();
      await loadMe();
    })();
  }, [loadMe]);
  if (state.kind === 'loading') return <AuthFrame pub={pub}><p className="pid-muted">{t('common.loading')}</p></AuthFrame>;
  if (state.kind === 'off') return <AuthFrame pub={pub} testId="portal-off"><h1>{t('identity.disabledPortal')}</h1></AuthFrame>;
  if (state.kind === 'out') return <PortalSignIn key={state.n} pub={pub} notice={state.notice} onSignedIn={(me) => setState({ kind: 'in', me })} />;
  return <>{children({
    me: state.me,
    signOut: async () => {
      await portalApi('/auth/logout', { method: 'POST', json: {} }).catch(() => undefined);
      out();
    },
    refreshMe: loadMe,
    sessionEnded: () => out(),
  })}</>;
}
